// エージェント間通信バス
// 各エージェント間でメッセージをやり取りするための中央バス

export type AgentType = 
  | 'demand_forecast'   // 需要予測
  | '52week_md'        // 52週MDカレンダー
  | 'inventory'        // 在庫最適化
  | 'planogram'        // 棚割り改善
  | 'flyer'           // チラシ作成
  | 'planning';       // 企画

export type MessageType = 
  | 'data_request'     // データ要求
  | 'data_response'    // データ応答
  | 'notification'     // 通知
  | 'alert'           // アラート
  | 'sync'            // 同期
  | 'update';         // 更新

export interface AgentMessage {
  id: string;
  timestamp: Date;
  from: AgentType;
  to: AgentType | 'broadcast';
  type: MessageType;
  topic: string;
  payload: any;
  priority?: 'low' | 'medium' | 'high' | 'critical';
}

export interface AgentSubscription {
  agentId: AgentType;
  topics: string[];
  callback: (message: AgentMessage) => void;
}

class AgentCommunicationBus {
  private static instance: AgentCommunicationBus;
  private subscriptions: Map<AgentType, AgentSubscription[]> = new Map();
  private messageQueue: AgentMessage[] = [];
  private messageHistory: AgentMessage[] = [];
  private maxHistorySize = 100;

  private constructor() {}

  static getInstance(): AgentCommunicationBus {
    if (!AgentCommunicationBus.instance) {
      AgentCommunicationBus.instance = new AgentCommunicationBus();
    }
    return AgentCommunicationBus.instance;
  }

  // エージェントの登録
  register(agentId: AgentType, topics: string[], callback: (message: AgentMessage) => void) {
    const subscription: AgentSubscription = {
      agentId,
      topics,
      callback
    };

    if (!this.subscriptions.has(agentId)) {
      this.subscriptions.set(agentId, []);
    }
    
    this.subscriptions.get(agentId)?.push(subscription);
    
    console.log(`[AgentBus] ${agentId} registered for topics:`, topics);
  }

  // メッセージの送信
  send(message: Omit<AgentMessage, 'id' | 'timestamp'>): string {
    const fullMessage: AgentMessage = {
      ...message,
      id: this.generateMessageId(),
      timestamp: new Date()
    };

    this.messageQueue.push(fullMessage);
    this.processMessage(fullMessage);
    
    // 履歴に追加
    this.addToHistory(fullMessage);
    
    return fullMessage.id;
  }

  // メッセージの処理
  private processMessage(message: AgentMessage) {
    if (message.to === 'broadcast') {
      // 全エージェントに送信
      this.subscriptions.forEach((subs, agentId) => {
        if (agentId !== message.from) {
          this.deliverToAgent(message, subs);
        }
      });
    } else {
      // 特定エージェントに送信
      const targetSubs = this.subscriptions.get(message.to as AgentType);
      if (targetSubs) {
        this.deliverToAgent(message, targetSubs);
      }
    }
  }

  // エージェントへの配信
  private deliverToAgent(message: AgentMessage, subscriptions: AgentSubscription[]) {
    subscriptions.forEach(sub => {
      // トピックがマッチするか、または全トピック購読（*）の場合
      if (sub.topics.includes('*') || sub.topics.includes(message.topic)) {
        setTimeout(() => sub.callback(message), 0); // 非同期実行
      }
    });
  }

  // データ要求のヘルパーメソッド
  async request(
    from: AgentType, 
    to: AgentType, 
    topic: string, 
    payload: any
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      const messageId = this.send({
        from,
        to,
        type: 'data_request',
        topic,
        payload
      });

      // タイムアウト設定（5秒）
      const timeout = setTimeout(() => {
        reject(new Error(`Request timeout: ${messageId}`));
      }, 5000);

      // 一時的なレスポンスリスナーを設定
      const tempCallback = (message: AgentMessage) => {
        if (
          message.type === 'data_response' && 
          message.payload?.requestId === messageId
        ) {
          clearTimeout(timeout);
          resolve(message.payload.data);
        }
      };

      // 一時的に登録
      this.register(from, [topic + '_response'], tempCallback);
    });
  }

  // メッセージ履歴の取得
  getHistory(filter?: {
    from?: AgentType;
    to?: AgentType;
    topic?: string;
    limit?: number;
  }): AgentMessage[] {
    let filtered = [...this.messageHistory];
    
    if (filter?.from) {
      filtered = filtered.filter(m => m.from === filter.from);
    }
    if (filter?.to) {
      filtered = filtered.filter(m => m.to === filter.to);
    }
    if (filter?.topic) {
      filtered = filtered.filter(m => m.topic === filter.topic);
    }
    if (filter?.limit) {
      filtered = filtered.slice(-filter.limit);
    }
    
    return filtered;
  }

  // 履歴に追加
  private addToHistory(message: AgentMessage) {
    this.messageHistory.push(message);
    
    // 履歴サイズを制限
    if (this.messageHistory.length > this.maxHistorySize) {
      this.messageHistory = this.messageHistory.slice(-this.maxHistorySize);
    }
  }

  // メッセージIDの生成
  private generateMessageId(): string {
    return `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  // クリーンアップ
  unregister(agentId: AgentType) {
    this.subscriptions.delete(agentId);
    console.log(`[AgentBus] ${agentId} unregistered`);
  }

  // 統計情報の取得
  getStats() {
    return {
      totalMessages: this.messageHistory.length,
      queueSize: this.messageQueue.length,
      activeAgents: Array.from(this.subscriptions.keys()),
      messagesByAgent: this.getMessageCountByAgent()
    };
  }

  private getMessageCountByAgent(): Record<AgentType, number> {
    const counts: Partial<Record<AgentType, number>> = {};
    
    this.messageHistory.forEach(msg => {
      counts[msg.from] = (counts[msg.from] || 0) + 1;
    });
    
    return counts as Record<AgentType, number>;
  }
}

export default AgentCommunicationBus.getInstance();
